import React, { useCallback, useState } from 'react'
import { faDownload } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import type { IconProp } from '@fortawesome/fontawesome-svg-core'
import type { AxiosResponse } from 'axios'
import FileDownload from 'js-file-download'
import { ButtonV2 } from '@labrav/react-components'
import { useLocation } from 'react-router-dom'
import { updateAppAlert, useTracker } from '@labrav/common'
import { useDispatch, useSelector } from '../../../store'
import {
  downloadPrivateOfferPdf,
  downloadResellerAuthorizationPdf,
  downloadSubscriptionContractPdf,
} from '../../../api/marketplace'
import { useTimeZone } from '../../utils/hooks/useTimeZone'
import { useDownloadDetailPdfButtonStyles } from './DownloadDetailPdfButton.styles'
import {
  DetailPdfPageType,
  DetailPdfSurface,
  type UseDownloadDetailPdfParams,
} from './downloadDetailPdf.types'

export {
  DetailPdfCloud,
  DetailPdfPageType,
  DetailPdfSurface,
  type UseDownloadDetailPdfParams,
} from './downloadDetailPdf.types'

type DetailPdfUserProfile = {
  firstName?: string
  lastName?: string
  givenName?: string
  email?: string
  timeZone?: string
  defaultTimeZone?: string
  name?: string
  displayName?: string
}

const TIME_ZONE_ALIASES: Record<string, string> = {
  'Asia/Calcutta': 'Asia/Kolkata',
}

const DEFAULT_PDF_FILENAME = 'Labra_download.pdf'

export const normalizeDetailPdfTimeZone = (timeZone: string): string => {
  const zone = (timeZone && timeZone.trim()) || 'UTC'
  return TIME_ZONE_ALIASES[zone] ?? zone
}

export const getDetailPdfDownloadedByName = (
  user: DetailPdfUserProfile
): string => {
  const fullName = [user.firstName, user.lastName]
    .filter(Boolean)
    .join(' ')
    .trim()

  if (fullName) return fullName

  return user.name || user.displayName || user.givenName || user.email || ''
}

export const getDetailPdfSurface = (pathname: string): DetailPdfSurface => {
  if (pathname.includes('/external-apps/hubspot/')) {
    return DetailPdfSurface.HUBSPOT
  }
  if (pathname.includes('/external-apps/salesforce/')) {
    return DetailPdfSurface.CANVAS
  }
  return DetailPdfSurface.PLATFORM
}

const getFilenameFromResponse = (
  response: AxiosResponse<Blob>,
  fallbackFilename = DEFAULT_PDF_FILENAME
): string => {
  const disposition = response.headers['content-disposition']
  if (!disposition) return fallbackFilename

  const match = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/i.exec(disposition)
  if (!match?.[1]) return fallbackFilename

  return match[1].replace(/['"]/g, '').trim() || fallbackFilename
}

export const saveDetailPdfBlob = (response: AxiosResponse<Blob>): void => {
  const filename = getFilenameFromResponse(response)
  FileDownload(response.data, filename)
}

const isPdfResponse = (contentType: string | undefined): boolean =>
  Boolean(contentType?.toLowerCase().includes('application/pdf'))

const getErrorStatus = (error: unknown): number | undefined => {
  if (typeof error === 'object' && error !== null && 'response' in error) {
    const response = (error as { response?: { status?: number } }).response
    return response?.status
  }
  return undefined
}

export const getDetailPdfErrorMessage = (error: unknown): string => {
  const status = getErrorStatus(error)

  if (status === 404) {
    return 'Unable to download PDF. Record not found.'
  }
  if (status === 400) {
    return 'Invalid timezone.'
  }
  return 'Failed to generate PDF. Please try again.'
}

type FetchDetailPdfParams = {
  pageType: DetailPdfPageType
  partnerId: string
  entityId: string
  timeZone: string
  downloadedBy?: string
}

export const fetchDetailPdf = ({
  pageType,
  partnerId,
  entityId,
  timeZone,
  downloadedBy,
}: FetchDetailPdfParams): Promise<AxiosResponse<Blob>> => {
  const queryParams = {
    timeZone,
    downloadedBy,
  }

  switch (pageType) {
    case DetailPdfPageType.CONTRACT:
      return downloadSubscriptionContractPdf({
        partnerId,
        subscriptionId: entityId,
        ...queryParams,
      })
    case DetailPdfPageType.PRIVATE_OFFER:
      return downloadPrivateOfferPdf({
        partnerId,
        privateOfferId: entityId,
        ...queryParams,
      })
    case DetailPdfPageType.RESELLER_AUTH:
      return downloadResellerAuthorizationPdf({
        partnerId,
        awsResellerAgreementId: entityId,
        ...queryParams,
      })
    default:
      return Promise.reject(new Error('Unsupported page type for PDF download'))
  }
}

export const useDownloadDetailPdf = ({
  pageType,
  partnerId,
  entityId,
  cloud,
}: UseDownloadDetailPdfParams) => {
  const dispatch = useDispatch()
  const tracker = useTracker()
  const { pathname } = useLocation()
  const { timeZone: profileTimeZone } = useTimeZone()
  const userProfile = useSelector(
    state => state.userProfile.userProfile as DetailPdfUserProfile
  )
  const companyName = useSelector(state => {
    const sellerInfo = state.flyoutSellerInfo as
      | { awsAccountName?: string }
      | undefined
    return (
      sellerInfo?.awsAccountName ||
      state.AccountData?.user?.partnerData?.companyName ||
      ''
    )
  })
  const [isDownloading, setIsDownloading] = useState(false)

  const downloadDetailPdf = useCallback(async () => {
    if (isDownloading) return

    if (!partnerId || !entityId) {
      dispatch(
        updateAppAlert({
          message: 'Details are not loaded yet. Please try again.',
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      return
    }

    setIsDownloading(true)

    const rawTimeZone =
      profileTimeZone ||
      userProfile.timeZone ||
      userProfile.defaultTimeZone ||
      'UTC'
    const effectiveTimeZone = normalizeDetailPdfTimeZone(rawTimeZone)
    const downloadedBy = getDetailPdfDownloadedByName(userProfile)
    const surface = getDetailPdfSurface(pathname)

    try {
      const response = await fetchDetailPdf({
        pageType,
        partnerId,
        entityId,
        timeZone: effectiveTimeZone,
        downloadedBy: downloadedBy || undefined,
      })

      const contentTypeHeader = response.headers['content-type']
      if (
        !isPdfResponse(
          typeof contentTypeHeader === 'string' ? contentTypeHeader : undefined
        )
      ) {
        throw new Error('Backend did not return a PDF')
      }

      saveDetailPdfBlob(response)

      tracker?.track({
        customTracking: true,
        section: 'Detail PDF Downloaded',
        component: '',
        action: '',
        page_type: pageType,
        cloud,
        partner_id: partnerId,
        company_name: companyName,
        user_name: downloadedBy,
        entity_id: entityId,
        surface,
      })
    } catch (error) {
      console.error('Detail PDF download failed:', error)
      dispatch(
        updateAppAlert({
          message: getDetailPdfErrorMessage(error),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
    } finally {
      setIsDownloading(false)
    }
  }, [
    cloud,
    companyName,
    dispatch,
    entityId,
    isDownloading,
    pageType,
    partnerId,
    pathname,
    profileTimeZone,
    tracker,
    userProfile,
  ])

  return { downloadDetailPdf, isDownloading }
}

type DownloadDetailPdfButtonProps = UseDownloadDetailPdfParams & {
  testId?: string
  className?: string
  label?: string
  onDownloadClick?: () => void
  // FontAwesome and React may resolve different csstype versions in this package.
  iconStyle?: Record<string, string | number | undefined>
}

export const DownloadDetailPdfButton: React.FC<
  DownloadDetailPdfButtonProps
> = ({
  pageType,
  partnerId,
  entityId,
  cloud,
  testId = 'download-detail-pdf',
  className,
  label = 'Download PDF',
  onDownloadClick,
  iconStyle,
}) => {
  const { classes } = useDownloadDetailPdfButtonStyles()
  const { downloadDetailPdf, isDownloading } = useDownloadDetailPdf({
    pageType,
    partnerId,
    entityId,
    cloud,
  })

  return (
    <ButtonV2
      styleType="link"
      className={className ?? classes.linkItem}
      smallVariant
      onClick={() => {
        onDownloadClick?.()
        void downloadDetailPdf()
      }}
      disabled={isDownloading || !partnerId || !entityId}
      loading={isDownloading}
      data-testid={testId}
    >
      <FontAwesomeIcon
        icon={faDownload as IconProp}
        className={classes.icon}
        // FontAwesome and React may resolve different csstype versions in this package.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        style={iconStyle as any}
      />
      {label}
    </ButtonV2>
  )
}
